import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  ckConfirm,
  Dialog,
  Field,
  MenuButton,
  Select,
  Sheet,
  SimpleTable,
  Spinner,
  Tag,
  TextInput,
  useCkToast,
  type CkMenuItem,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type DealDetail, type DealDto, type DealPayment } from "../../api/salesApi";
import { useShell } from "../shell/ShellContext";
import { DealDialog } from "./DealDialog";
import { CHANNEL_LABEL, DEAL_STATUS, errorMessage, fmtDate, fmtIls, fmtMoney, fromMinor, REGION_LABEL, toMinor, todayIso, withEmpty } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";

/** «רישום תשלום» — amount ₪, date, method, note */
export function PaymentDialog({ deal, open, onClose, onSaved }: { deal: Pick<DealDto, "id" | "balanceAgorot" | "paymentMethodKey"> | null; open: boolean; onClose: () => void; onSaved: (d: DealDetail) => void }) {
  const toast = useCkToast();
  const { optionsOf } = useSalesMeta();
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState(todayIso());
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !deal) return;
    setAmount(fromMinor(Math.max(0, deal.balanceAgorot)));
    setPaidOn(todayIso());
    setMethod(deal.paymentMethodKey ?? "");
    setNote("");
  }, [open, deal]);
  if (!deal) return null;
  const submit = async () => {
    const amt = toMinor(amount);
    if (!amt || amt <= 0) {
      toast.error("סכום לא תקין");
      return;
    }
    setBusy(true);
    try {
      const d = await salesApi.deals.addPayment(deal.id, { amountAgorot: amt, paidOn, methodKey: method || null, note: note.trim() || null });
      toast.success("התשלום נרשם");
      onSaved(d);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "רישום התשלום נכשל"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      icon="coin"
      title="רישום תשלום"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "שומר…" : "רישום"}
          </Button>
        </>
      }
    >
      <div className="sl-stack">
        <p className="sl-note">יתרה לתשלום: {fmtIls(deal.balanceAgorot)}</p>
        <div className="sl-grid2">
          <Field label="סכום (₪)">
            <TextInput ltr inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          </Field>
          <Field label="תאריך">
            <TextInput type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
          <Field label="צורת תשלום">
            <Select options={withEmpty("—", optionsOf("sales_payment_method"))} value={method} onChange={setMethod} aria-label="צורת תשלום" />
          </Field>
          <Field label="הערה">
            <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

/** The deal card (a side sheet): details, payments, edit / cancel / delete. */
export function DealSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const { allowed } = useShell();
  const canEdit = allowed("sales.edit");
  const canManage = allowed("sales.manage");
  const { label } = useSalesMeta();
  const [deal, setDeal] = useState<DealDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = useCallback(() => {
    salesApi.deals
      .get(id)
      .then((d) => {
        setDeal(d);
        setError(null);
      })
      .catch((e) => setError(errorMessage(e, "העסקה לא נמצאה")));
  }, [id]);
  useEffect(() => {
    setDeal(null);
    load();
  }, [load]);

  const changed = (d: DealDetail) => {
    setDeal(d);
    onChanged();
  };

  const run = async (fn: () => Promise<DealDetail>, ok: string) => {
    try {
      changed(await fn());
      toast.success(ok);
    } catch (e) {
      toast.error(errorMessage(e, "הפעולה נכשלה"));
    }
  };

  const removePayment = async (p: DealPayment) => {
    if (!deal) return;
    if (!(await ckConfirm({ title: "למחוק את התשלום?", message: `${fmtIls(p.amountAgorot)} מ־${fmtDate(p.paidOn)} יימחק והיתרה תתעדכן.`, confirmLabel: "מחיקה", danger: true }))) return;
    await run(() => salesApi.deals.removePayment(deal.id, p.id), "התשלום נמחק");
  };

  const remove = async () => {
    if (!deal) return;
    if (!(await ckConfirm({ title: "למחוק את העסקה?", message: "העסקה והתשלומים שלה יימחקו לגמרי. (כדי לשמור תיעוד — בטלו אותה במקום.)", confirmLabel: "מחיקה", danger: true }))) return;
    try {
      await salesApi.deals.remove(deal.id);
      toast.success("העסקה נמחקה");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "המחיקה נכשלה"));
    }
  };

  const menu: CkMenuItem[] = deal
    ? [
        ...(canEdit
          ? deal.status === "cancelled"
            ? [{ key: "restore", label: "שחזור העסקה", icon: "undo", onClick: () => void run(() => salesApi.deals.restore(deal.id), "העסקה שוחזרה") }]
            : [
                {
                  key: "cancel",
                  label: "ביטול העסקה",
                  icon: "x",
                  onClick: () =>
                    void ckConfirm({ title: "לבטל את העסקה?", message: "העסקה תישאר בתיעוד אבל לא תיספר במכירות וביתרות.", confirmLabel: "ביטול העסקה", danger: true }).then(
                      (ok) => {
                        if (ok) void run(() => salesApi.deals.cancel(deal.id), "העסקה בוטלה");
                      },
                    ),
                },
              ]
          : []),
        ...(deal.leadId ? [{ key: "lead", label: "לליד", icon: "phone", onClick: () => navigate(`/sales/leads/${deal.leadId}`) }] : []),
        { key: "customer", label: "לכרטיס הלקוח", icon: "user", onClick: () => navigate(`/customers?card=${deal.customerId}`) },
        ...(canManage ? [{ divider: true as const }, { key: "del", label: "מחיקה", icon: "trash", danger: true, onClick: () => void remove() }] : []),
      ]
    : [];

  const st = deal ? DEAL_STATUS[deal.status] : null;
  return (
    <Sheet open onClose={onClose} width={620} ariaLabel="עסקה">
      <div className="sl-sheet">
        <div className="sl-sheet__head">
          <div className="sl-sheet__title">
            <b>{deal ? deal.productName : "עסקה"}</b>
            {deal ? (
              <span className="sl-muted">
                {deal.customerName || "—"} · {fmtDate(deal.dealOn)}
              </span>
            ) : null}
          </div>
          <div className="sl-row">
            {deal && canEdit && deal.status !== "cancelled" ? (
              <Button size="sm" icon="edit" onClick={() => setEditing(true)}>
                עריכה
              </Button>
            ) : null}
            {deal ? (
              <MenuButton align="end" width={200} items={menu} renderButton={(p) => <Button {...p} size="sm" variant="ghost" icon="more" iconOnly aria-label="פעולות" />} />
            ) : null}
            <Button size="sm" variant="ghost" icon="x" iconOnly aria-label="סגירה" onClick={onClose} />
          </div>
        </div>
        <div className="sl-sheet__body">
          {error ? <Banner tone="danger">{error}</Banner> : null}
          {!deal && !error ? (
            <div className="sl-busy">
              <Spinner size={24} />
            </div>
          ) : null}
          {deal && st ? (
            <>
              <div className="sl-row">
                <Tag tone={st.tone}>{st.label}</Tag>
                <Tag tone="neutral">{REGION_LABEL[deal.region]}</Tag>
                <Tag tone="neutral">{deal.channel === "rep" ? deal.repName || "נציג" : CHANNEL_LABEL[deal.channel]}</Tag>
              </div>
              <dl className="sl-kv">
                <div>
                  <dt>סה״כ</dt>
                  <dd className="sl-num">
                    <b>{fmtIls(deal.totalIlsAgorot)}</b>
                    {deal.currency !== "ILS" ? <small className="sl-muted"> ({fmtMoney(deal.listPriceAgorot - deal.discountAgorot, deal.currency)} × {deal.fxRate})</small> : null}
                  </dd>
                </div>
                <div>
                  <dt>שולם</dt>
                  <dd className="sl-num sl-success">{fmtIls(deal.paidAgorot)}</dd>
                </div>
                <div>
                  <dt>יתרה</dt>
                  <dd className={`sl-num${deal.balanceAgorot > 0 ? " sl-danger" : ""}`}>{fmtIls(deal.balanceAgorot)}</dd>
                </div>
                <div>
                  <dt>מתוכו מע״מ</dt>
                  <dd className="sl-num">{deal.vatAgorot ? fmtIls(deal.vatAgorot) : "—"}</dd>
                </div>
                <div>
                  <dt>מחיר מחירון</dt>
                  <dd className="sl-num">{fmtMoney(deal.listPriceAgorot, deal.currency)}</dd>
                </div>
                <div>
                  <dt>הנחה</dt>
                  <dd className="sl-num">{deal.discountAgorot ? fmtMoney(deal.discountAgorot, deal.currency) : "—"}</dd>
                </div>
                <div>
                  <dt>תשלומים</dt>
                  <dd>{deal.installments}</dd>
                </div>
                <div>
                  <dt>צורת תשלום</dt>
                  <dd>{label("sales_payment_method", deal.paymentMethodKey) || "—"}</dd>
                </div>
                <div>
                  <dt>טלפון</dt>
                  <dd className="sl-ltr">{deal.customerPhone || "—"}</dd>
                </div>
                <div>
                  <dt>מקור</dt>
                  <dd>{label("sales_lead_source", deal.source) || "—"}</dd>
                </div>
              </dl>
              {deal.notes ? <p className="sl-note">{deal.notes}</p> : null}

              <div className="sl-row sl-row--between">
                <h4 className="sl-section-title">תשלומים</h4>
                {canEdit && deal.status !== "cancelled" ? (
                  <Button size="sm" icon="plus" variant={deal.balanceAgorot > 0 ? "primary" : "default"} onClick={() => setPaying(true)}>
                    רישום תשלום
                  </Button>
                ) : null}
              </div>
              <SimpleTable
                ariaLabel="תשלומים"
                rows={deal.payments}
                rowKey="id"
                empty="עוד לא נרשמו תשלומים"
                columns={[
                  { key: "paidOn", label: "תאריך", render: (p: DealPayment) => fmtDate(p.paidOn) },
                  { key: "amount", label: "סכום", align: "end", render: (p: DealPayment) => <span className="sl-num">{fmtIls(p.amountAgorot)}</span> },
                  { key: "method", label: "צורה", render: (p: DealPayment) => label("sales_payment_method", p.methodKey) || "—" },
                  { key: "note", label: "הערה", render: (p: DealPayment) => <span className="sl-cell2"><span>{p.note || ""}</span>{p.byName ? <small>{p.byName}</small> : null}</span> },
                  ...(canEdit
                    ? [{ key: "x", label: "", width: 44, render: (p: DealPayment) => <Button size="sm" variant="ghost" icon="trash" iconOnly aria-label="מחיקת תשלום" onClick={() => void removePayment(p)} /> }]
                    : []),
                ]}
              />
            </>
          ) : null}
        </div>
      </div>
      <DealDialog open={editing} deal={deal} onClose={() => setEditing(false)} onSaved={changed} />
      <PaymentDialog open={paying} deal={deal} onClose={() => setPaying(false)} onSaved={changed} />
    </Sheet>
  );
}
